import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiBarChart2, FiDollarSign, FiFileText, FiTrendingUp } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getParentMenuItems } from '../../components/layout/ParentMenuConfig';
import EducationalParticles from '../../components/common/EducationalParticles';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  parentDashboardAPI,
  parentStudentAPI,
  parentGradeAPI,
} from '../../api/parentEndpoints';
import OverviewDashboard from '../parent/analytics/OverviewDashboard';
import { 
  DashboardContainer, 
  DashboardHeader, 
  StatsGrid, 
  StatCard, 
  StatIcon, 
  StatInfo, 
  SectionTitle,
  EmptyState,
} from './Dashboard.styles';

const ParentDashboard = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedStudentForAnalytics, setSelectedStudentForAnalytics] = useState(null);

  // Use centralized menu configuration
  const menuItems = getParentMenuItems();

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, studentsRes, gradesRes] = await Promise.all([
        parentDashboardAPI.getDashboard(),
        parentStudentAPI.getStudents(),
        parentGradeAPI.getGrades(),
      ]);
      
      setStats(dashboardRes.data.statistics);
      setStudents(studentsRes.data.students || []);
      setGrades(gradesRes.data.grades || []);
      
      // Auto-select first student for analytics
      const studentsList = studentsRes.data.students || [];
      if (studentsList.length > 0) {
        setSelectedStudentForAnalytics(studentsList[0].id);
      }
    } catch (error) {
      showToast('error', 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <LoadingSpinner size="large" text="Loading dashboard..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Parent Dashboard 👨‍👩‍👧‍👦</h1>
            <p>Monitor your children's learning progress and manage their education</p>
          </div>
        </DashboardHeader>

        {stats && (
        <StatsGrid>
          <StatCard>
            <StatIcon color="#2c5f34">
              <FiUsers />
            </StatIcon>
            <StatInfo>
                <h3>{stats.total_students || 0}</h3>
                <p>Total Students</p>
            </StatInfo>
          </StatCard>

          <StatCard>
              <StatIcon color="#EF4444">
                <FiDollarSign />
            </StatIcon>
            <StatInfo>
                <h3>KES {parseFloat(stats.monthly_spending || 0).toLocaleString()}</h3>
                <p>Monthly Spending</p>
            </StatInfo>
          </StatCard>

          <StatCard>
              <StatIcon color="#10B981">
                <FiFileText />
            </StatIcon>
            <StatInfo>
                <h3>{stats.active_contents || 0}</h3>
                <p>Active Contents</p>
            </StatInfo>
          </StatCard>

          <StatCard>
              <StatIcon color="#F59E0B">
                <FiTrendingUp />
            </StatIcon>
            <StatInfo>
                <h3>{students.length > 0 ? 'Active' : 'No Students'}</h3>
                <p>Account Status</p>
            </StatInfo>
          </StatCard>
        </StatsGrid>
        )}

        {/* Analytics Overview */}
        <div style={{ marginTop: '40px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '24px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <SectionTitle style={{ margin: 0 }}>
              <FiBarChart2 />
              Learning Analytics
            </SectionTitle>
            
            {students.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label 
                  htmlFor="student-select" 
                  style={{ 
                    color: theme.colors.textSecondary,
                    fontSize: '14px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Student:
                </label>
                <select
                  id="student-select"
                  value={selectedStudentForAnalytics || ''}
                  onChange={(e) => setSelectedStudentForAnalytics(e.target.value)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.colors.border}`,
                    background: theme.colors.surface,
                    color: theme.colors.textPrimary,
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minWidth: '200px',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = theme.colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = theme.colors.border;
                  }}
                >
                  {students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.username} - {student.grade}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {selectedStudentForAnalytics && students.length > 0 ? (
            <div style={{
              background: theme.colors.surface,
              borderRadius: '16px',
              padding: '24px',
              border: `1px solid ${theme.colors.border}`,
            }}>
              <OverviewDashboard 
                studentId={selectedStudentForAnalytics}
                studentName={students.find(s => s.id === selectedStudentForAnalytics)?.username || 'Student'}
              />
            </div>
          ) : (
            <EmptyState>
              <FiBarChart2 size={48} />
              <h3>No Students Added</h3>
              <p>Add students to view their learning analytics</p>
              <Button 
                leftIcon={<FiUsers />} 
                onClick={() => navigate('/parent/students')}
              >
                Add Student
              </Button>
            </EmptyState>
          )}
        </div>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default ParentDashboard;
